import { useIsMobile } from "../hooks/useIsMobile";
import {
  collection,
  doc,
  getDoc,
  limit,
  orderBy,
  query,
  QueryConstraint,
  where,
} from "firebase/firestore";
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  Clock,
  Copy,
  FileText,
  ImageIcon,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePostsListPage } from "../hooks/usePostsListPage";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAnalyticsUiEnabled } from "../hooks/useAnalyticsUiEnabled";
import { usePostActions } from "../hooks/usePostActions";
import { usePostAnalyticsForPosts } from "../hooks/usePostAnalyticsForPosts";
import { cn, formatDate, loadMediaUrls } from "../lib/utils";
import { PlatformType } from "../models/Platform";
import { Post, isVideoPost } from "../models/Post";
import { postConverter } from "../services/converters";
import { db } from "../services/Firebase";
import { analyzeConnectionTimes } from "../utils/connectionTimes";
import { isPostEditable } from "../utils/postStatus";
import { postsListStatus } from "../utils/postsListStatus";
import { PlatformConnectionIcon } from "./PlatformConnectionIcon";
import { PostMetricsSummary } from "./PostMetricsRow";
import { useWorkspaceConnections } from "../hooks/useWorkspaceConnections";
import { PostViewer } from "./PostViewer";
import { SlidePreview } from "./SlidePreview";
import { StatusBadge } from "./StatusBadge";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useToast } from "./ui/use-toast";

const PAGE_SIZE = 10;

const hasSameRenderedPostState = (
  cachedPost: Post,
  nextPost: Omit<Post, "id">,
) =>
  cachedPost.updatedAt === nextPost.updatedAt &&
  cachedPost.status === nextPost.status &&
  JSON.stringify(cachedPost.connections) === JSON.stringify(nextPost.connections) &&
  isVideoPost(cachedPost) === isVideoPost(nextPost) &&
  cachedPost.videoChoreographyHash === nextPost.videoChoreographyHash &&
  cachedPost.videoMusicPath === nextPost.videoMusicPath &&
  cachedPost.videoAudioMuted === nextPost.videoAudioMuted &&
  cachedPost.videoSpeed === nextPost.videoSpeed;

// Helper to format time only (e.g., "2:00 PM")
const formatTimeOnly = (timestamp: number): string => {
  return new Date(timestamp).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

// Define the status types we want to include in the "all" filter for the dashboard
const ALL_DASHBOARD_STATUSES = [
  "scheduled",
  "published",
  "failed",
  "mixed",
  "publishing",
  "pre-processing",
  "pending",
];

export type ContentListFilter = "all" | "scheduled" | "published" | "failed";

export interface ContentListProps {
  embedded?: boolean;
  filter?: ContentListFilter;
  limitCount?: number;
  onFilterChange?: (filter: ContentListFilter) => void;
  onLimitCountChange?: (limitCount: number) => void;
}

export default function ContentList({
  embedded = false,
  filter: propFilter,
  limitCount: propLimitCount,
  onFilterChange,
  onLimitCountChange,
}: ContentListProps) {
  const { currentUser, activeWorkspaceId } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const {
    handleDuplicate,
    getDeleteDialog,
    handleEdit: handleEditAction,
  } = usePostActions();

  const [filter, setFilter] = useState<ContentListFilter>(propFilter ?? "all");
  const [limitCount, setLimitCount] = useState(propLimitCount ?? PAGE_SIZE);

  const [selectedPostForViewer, setSelectedPostForViewer] =
    useState<Post | null>(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(
    new Set(),
  );
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});

  const [searchParams, setSearchParams] = useSearchParams();

  const changeFilter = useCallback(
    (nextFilter: ContentListFilter) => {
      setFilter(nextFilter);
      setLimitCount(PAGE_SIZE);
      setMediaUrls({});
      onFilterChange?.(nextFilter);
      onLimitCountChange?.(PAGE_SIZE);
    },
    [onFilterChange, onLimitCountChange],
  );

  const changeLimitCount = useCallback(
    (nextLimitCount: number) => {
      setLimitCount(nextLimitCount);
      onLimitCountChange?.(nextLimitCount);
    },
    [onLimitCountChange],
  );

  useEffect(() => {
    if (propFilter) {
      setFilter(propFilter);
    }
  }, [propFilter]);

  useEffect(() => {
    if (propLimitCount) {
      setLimitCount(propLimitCount);
    }
  }, [propLimitCount]);

  // Handle viewPost query param - auto-open PostViewer for a specific post
  useEffect(() => {
    const viewPostId = searchParams.get("viewPost");
    console.log(
      "[ContentList] viewPost effect - viewPostId:",
      viewPostId,
      "currentUser:",
      activeWorkspaceId,
    );
    if (!viewPostId || !currentUser) return;

    const fetchAndOpenPost = async () => {
      try {
        console.log("[ContentList] Fetching post:", viewPostId);
        const postRef = doc(db, "posts", viewPostId).withConverter(
          postConverter,
        );
        const postSnap = await getDoc(postRef);

        console.log(
          "[ContentList] Post exists:",
          postSnap.exists(),
          "data userId:",
          postSnap.data()?.userId,
        );
        if (postSnap.exists() && postSnap.data().userId === activeWorkspaceId) {
          const postData = { ...postSnap.data(), id: viewPostId };
          console.log("[ContentList] Opening viewer for post:", postData.id);
          setSelectedPostForViewer(postData);
          setIsViewerOpen(true);
        } else {
          toast({
            title: "Post not found",
            description:
              "The post you're looking for doesn't exist or you don't have access to it.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error fetching post for viewer:", error);
        toast({
          title: "Error",
          description: "Failed to load the post.",
          variant: "destructive",
        });
      }

      // Clear the viewPost param from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("viewPost");
      setSearchParams(newParams, { replace: true });
    };

    fetchAndOpenPost();
  }, [searchParams, currentUser, activeWorkspaceId, setSearchParams, toast]);

  const { connections: workspaceConnections } = useWorkspaceConnections();

  const connectionsById = useMemo(() => {
    return Object.fromEntries(
      workspaceConnections.map((connection) => [connection.id, connection]),
    );
  }, [workspaceConnections]);

  // Function to build the Firestore query
  const buildQuery = useCallback(
    (currentLimit: number) => {
      if (!currentUser) return null;

      const constraints: QueryConstraint[] = [
        where("userId", "==", activeWorkspaceId),
      ];

      // Add status filter based on the selected tab
      if (filter === "scheduled") {
        constraints.push(where("status", "==", "scheduled"));
      } else if (filter === "published") {
        constraints.push(
          where("status", "in", ["published", "failed", "mixed"]),
        );
      } else if (filter === "failed") {
        constraints.push(where("status", "in", ["failed", "mixed"]));
      } else {
        // "all" filter - includes all non-draft/non-generating statuses
        constraints.push(where("status", "in", ALL_DASHBOARD_STATUSES));
      }

      constraints.push(
        orderBy("updatedAt", "desc"),
        orderBy("__name__", "desc"),
        limit(currentLimit),
      );

      return query(
        collection(db, "posts").withConverter(postConverter),
        ...constraints,
      );
    },
    [currentUser, activeWorkspaceId, filter],
  );

  const { snapshot, loading: fillingPage, error, hasMore } = usePostsListPage(
    buildQuery, limitCount,
  );

  // Maintain stable post references to prevent unnecessary re-renders
  const postsMapRef = useRef<Map<string, Post>>(new Map());

  const eligiblePosts: Post[] = useMemo(() => {
    if (!snapshot) return [];

    const newPosts: Post[] = [];
    snapshot.docs.forEach((doc) => {
      const postId = doc.id;
      const postData = doc.data() as Omit<Post, "id">;
      if (!postsListStatus(postData)) return;
      const existingPost = postsMapRef.current.get(postId);

      if (existingPost && hasSameRenderedPostState(existingPost, postData)) {
        newPosts.push(existingPost);
      } else {
        const newPost = { ...postData, id: postId } as Post;
        postsMapRef.current.set(postId, newPost);
        newPosts.push(newPost);
      }
    });

    return newPosts;
  }, [snapshot]);

  const posts = useMemo(
    () => eligiblePosts.slice(0, limitCount),
    [eligiblePosts, limitCount],
  );
  // Engagement metrics for the visible page, batched (only posts with at
  // least one published connection have analytics docs to fetch). UI-gated
  // behind the engagement-analytics-ui flag; empty ids skip all queries.
  const analyticsUiEnabled = useAnalyticsUiEnabled();
  const publishedPostIds = useMemo(
    () =>
      analyticsUiEnabled
        ? posts
            .filter((p) =>
              Object.values(p.connections ?? {}).some(
                (c) => c.status === "published",
              ),
            )
            .map((p) => p.id)
        : [],
    [posts, analyticsUiEnabled],
  );
  const analyticsByPostId = usePostAnalyticsForPosts(publishedPostIds);

  useEffect(() => {
    const loadUrls = async () => {
      if (posts.length > 0) {
        // Only load URLs for media paths we haven't loaded yet
        const pathsToLoad = new Set<string>();

        posts.forEach((post) => {
          post.mediaItems?.forEach((mediaItem) => {
            if (mediaItem && !mediaUrls[mediaItem.path]) {
              pathsToLoad.add(mediaItem.path);
            }
            // Also check thumbnail paths for videos
            if (
              mediaItem?.type === "video" &&
              mediaItem.metadata?.thumbnailPath &&
              !mediaUrls[mediaItem.metadata.thumbnailPath]
            ) {
              pathsToLoad.add(mediaItem.metadata.thumbnailPath);
            }
          });
        });

        // Only make requests for paths we don't have yet
        if (pathsToLoad.size > 0) {
          // Filter posts to only include those with unloaded media
          const postsToLoad = posts.filter((post) =>
            post.mediaItems?.some(
              (item) =>
                item &&
                (pathsToLoad.has(item.path) ||
                  (item.type === "video" &&
                    item.metadata?.thumbnailPath &&
                    pathsToLoad.has(item.metadata.thumbnailPath))),
            ),
          );

          const urlEntries = await loadMediaUrls(postsToLoad);
          setMediaUrls((prev) => ({ ...prev, ...urlEntries }));
        }
      }
    };
    loadUrls();
  }, [posts]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = () => {
    if (hasMore && !fillingPage) {
      changeLimitCount(limitCount + PAGE_SIZE);
    }
  };

  const toggleDescription = (postId: string) => {
    setExpandedDescriptions((prev) => {
      const next = new Set(prev);
      if (next.has(postId)) {
        next.delete(postId);
      } else {
        next.add(postId);
      }
      return next;
    });
  };

  const truncateText = (text: string, maxLength: number) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  if (fillingPage && posts.length === 0) {
    return (
      <div className={cn(!embedded && "p-6")}>
        <Card className={cn(embedded && "border-0 shadow-none")}>
          {!embedded && (
            <CardHeader>
              <CardTitle>Posts</CardTitle>
            </CardHeader>
          )}
          <CardContent>
            <div className="flex items-center justify-center h-64">
              <div className="relative">
                <div className="h-10 w-10 rounded-full border-2 border-gray-300 border-t-[#FF5C35] animate-spin" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn(!embedded && "p-6")}>
        <Card className={cn(embedded && "border-0 shadow-none")}>
          {!embedded && (
            <CardHeader>
              <CardTitle>Posts</CardTitle>
            </CardHeader>
          )}
          <CardContent>
            <div className="text-center bg-red-50 p-6 rounded-xl max-w-md mx-auto">
              <h3 className="font-semibold text-red-700 mb-2">
                Error loading posts
              </h3>
              <p className="text-sm text-red-600 mb-4">{error.message}</p>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="border-red-200 hover:bg-red-100 text-red-700"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={cn(!embedded && "p-4 md:p-6")}>
      <Card className={cn(embedded && "border-0 shadow-none")}>
        <CardHeader className="flex flex-col items-start gap-4 mb-4 sm:flex-row sm:items-center sm:justify-between sm:mb-2">
          {!embedded && <CardTitle>Posts</CardTitle>}

          {/* Mobile Dropdown Filter */}
          <div className="block sm:hidden w-full">
            <Select
              value={filter}
              onValueChange={(value) =>
                changeFilter(value as ContentListFilter)
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Desktop Button Filters */}
          <div className="hidden sm:flex flex-row flex-wrap justify-start items-center gap-2">
            {(["all", "scheduled", "published", "failed"] as const).map((f) => (
              <Button
                key={f}
                variant={filter === f ? "default" : "outline"}
                onClick={() => changeFilter(f)}
                className="min-w-[100px] capitalize"
              >
                {f === "scheduled" && <Clock className="h-4 w-4 mr-2" />}
                {f === "published" && <CheckCircle className="h-4 w-4 mr-2" />}
                {f === "failed" && <AlertCircle className="h-4 w-4 mr-2" />}
                {f}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent>
          {!fillingPage && posts.length === 0 && (
            <div className="text-center max-w-md mx-auto py-10">
              <div className="mb-4">
                <ImageIcon className="h-12 w-12 text-gray-400 mx-auto" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">
                No posts found
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                {filter === "all"
                  ? "Your published and scheduled posts will show up here. Start by generating some drafts."
                  : `No ${filter} posts found.`}
              </p>
              <div className="flex items-center justify-center gap-3">
                <Button
                  onClick={() => navigate("/generate")}
                  className="shadow-sm hover:shadow-md transition-all duration-200"
                >
                  <Sparkles className="h-4 w-4 mr-1" />
                  Generate Content
                </Button>
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate("/create-post", { state: { isNewPost: true } })
                  }
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Blank Post
                </Button>
              </div>
            </div>
          )}

          {posts.length > 0 && (
            <div className="space-y-4">
              {posts.map((post) => {
                const displayText = post.description || post.title || "";
                const connectionStatuses = Object.values(post.connections);
                const hasPublished = connectionStatuses.some(
                  (conn) => conn.status === "published",
                );
                const isProcessing = connectionStatuses.some(
                  (conn) =>
                    conn.status === "publishing" ||
                    conn.status === "pre-processing",
                );
                const isEditable = isPostEditable(post);
                const derivedStatus = postsListStatus(post)!;

                // Analyze connection times for display
                const {
                  hasMultipleTimes,
                  hasMixedStates,
                  allSameDay,
                  connectionTimes,
                } = analyzeConnectionTimes(post);

                // Get pending connections with their times for display
                const pendingConnectionsWithTimes = connectionTimes.filter(
                  (c) => c.status !== "published" && c.scheduledFor,
                );

                // Determine date display strategy
                let dateLabel = "Updated";
                let dateValue = post.updatedAt;
                let dateIcon = <Calendar className="h-4 w-4 mr-1" />;
                let dateColorClass = "text-gray-500";
                let multipleTimes: number[] = [];

                if (derivedStatus === "published") {
                  const firstPublishedConnection = Object.values(
                    post.connections,
                  ).find((conn) => conn.publishedAt);
                  if (firstPublishedConnection?.publishedAt) {
                    dateLabel = "Published";
                    dateValue = firstPublishedConnection.publishedAt;
                  }
                } else if (
                  derivedStatus === "scheduled" &&
                  hasMultipleTimes &&
                  allSameDay
                ) {
                  // Multiple times on same day - show date once, list times
                  dateLabel = "Scheduled";
                  dateValue =
                    pendingConnectionsWithTimes[0]?.scheduledFor ||
                    post.scheduledFor!;
                  dateIcon = <Clock className="h-4 w-4 mr-1" />;
                  dateColorClass = "text-[#FF5C35]";
                  multipleTimes = [
                    ...new Set(
                      pendingConnectionsWithTimes.map((c) => c.scheduledFor!),
                    ),
                  ].sort((a, b) => a - b);
                } else if (derivedStatus === "scheduled" && !hasMultipleTimes) {
                  // Single time - use per-connection time (more accurate than post-level)
                  dateLabel = "Scheduled at";
                  dateValue =
                    pendingConnectionsWithTimes[0]?.scheduledFor ||
                    post.scheduledFor!;
                  dateIcon = <Clock className="h-4 w-4 mr-1" />;
                  dateColorClass = "text-[#FF5C35]";
                }

                return (
                  <div
                    key={post.id}
                    onClick={() => {
                      if (isMobile && isEditable) {
                        handleEditAction(post.id);
                      } else {
                        setSelectedPostForViewer(post);
                        setIsViewerOpen(true);
                      }
                    }}
                    className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-200 cursor-pointer"
                  >
                    <div className="p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                        {post.mediaItems?.[0] ? (
                          <div className="w-full sm:w-24 h-48 sm:h-28 flex items-center justify-center flex-shrink-0 rounded-lg overflow-hidden border border-gray-100 bg-gray-50 mb-3 sm:mb-0">
                            <SlidePreview
                              mediaItem={post.mediaItems[0]}
                              mediaUrls={mediaUrls}
                              className="h-full max-w-full"
                            />
                          </div>
                        ) : (
                          <div className="w-full sm:w-24 h-48 sm:h-28 rounded-lg overflow-hidden flex-shrink-0 bg-gray-50 flex items-center justify-center border border-gray-100 mb-3 sm:mb-0">
                            <FileText className="w-8 h-8 text-gray-400" />
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <div className="mb-3">
                            {displayText && (
                              <>
                                <p className="text-sm text-gray-700">
                                  <span className="notranslate" translate="no">
                                    {expandedDescriptions.has(post.id)
                                      ? displayText
                                      : truncateText(displayText, 150)}
                                  </span>
                                  {displayText.length > 150 && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleDescription(post.id);
                                      }}
                                      className="ml-1 text-[#FF5C35] hover:text-[#FF5C35]/80 font-medium"
                                    >
                                      {expandedDescriptions.has(post.id)
                                        ? "Show less"
                                        : "Show more"}
                                    </button>
                                  )}
                                </p>
                              </>
                            )}
                          </div>

                          <div className="flex flex-col gap-2">
                            {/* Platform badges - show with times/status when multiple times or mixed states */}
                            {hasMultipleTimes || hasMixedStates ? (
                              <div className="flex flex-wrap gap-2">
                                {connectionTimes
                                  .filter((c) => c.status !== "published")
                                  .map((connTime) => (
                                    <div
                                      key={connTime.connectionId}
                                      className="flex items-center gap-1.5 bg-gray-50 rounded-full px-2 py-1"
                                    >
                                      <PlatformConnectionIcon
                                        platform={connTime.platform}
                                        profileImage={
                                          connectionsById[connTime.connectionId]
                                            ?.profileImage
                                        }
                                        profileName={
                                          connectionsById[connTime.connectionId]
                                            ?.profileName
                                        }
                                        size="xs"
                                        disableTooltip
                                      />
                                      <span className="text-xs text-[#FF5C35] font-medium">
                                        {connTime.scheduledFor
                                          ? formatDate(connTime.scheduledFor)
                                          : "Not scheduled"}
                                      </span>
                                    </div>
                                  ))}
                                {/* Show published connections without time */}
                                {connectionTimes
                                  .filter((c) => c.status === "published")
                                  .map((connTime) => (
                                    <div
                                      key={connTime.connectionId}
                                      className="flex items-center gap-1.5 bg-green-50 rounded-full px-2 py-1"
                                    >
                                      <PlatformConnectionIcon
                                        platform={connTime.platform}
                                        profileImage={
                                          connectionsById[connTime.connectionId]
                                            ?.profileImage
                                        }
                                        profileName={
                                          connectionsById[connTime.connectionId]
                                            ?.profileName
                                        }
                                        size="xs"
                                        disableTooltip
                                      />
                                      <span className="text-xs text-green-600 font-medium">
                                        Published
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            ) : (
                              <div className="flex -space-x-2">
                                {Object.values(post.connections).map(
                                  (connection) => (
                                    <PlatformConnectionIcon
                                      key={connection.connectionId}
                                      platform={
                                        connection.platform as PlatformType
                                      }
                                      profileImage={
                                        connectionsById[connection.connectionId]
                                          ?.profileImage
                                      }
                                      profileName={
                                        connectionsById[connection.connectionId]
                                          ?.profileName
                                      }
                                      size="sm"
                                    />
                                  ),
                                )}
                              </div>
                            )}

                            <div className="flex flex-wrap items-center gap-4 text-sm">
                              <StatusBadge
                                status={derivedStatus}
                                mixedStatusCounts={
                                  derivedStatus === "mixed"
                                    ? Object.values(post.connections).reduce(
                                        (acc, conn) => {
                                          acc[conn.status] =
                                            (acc[conn.status] || 0) + 1;
                                          return acc;
                                        },
                                        {} as Record<string, number>,
                                      )
                                    : undefined
                                }
                                errorInfo={
                                  derivedStatus === "failed" &&
                                  Object.values(post.connections).some(
                                    (conn) => conn.error,
                                  )
                                    ? {
                                        code: Object.values(
                                          post.connections,
                                        ).find((conn) => conn.error)?.error
                                          ?.code,
                                        message: Object.values(
                                          post.connections,
                                        ).find((conn) => conn.error)?.error
                                          ?.message,
                                        timestamp:
                                          Object.values(post.connections).find(
                                            (conn) => conn.error,
                                          )?.error?.timestamp || 0,
                                        retryCount:
                                          Object.values(post.connections).find(
                                            (conn) => conn.error,
                                          )?.error?.retryCount || 0,
                                        retryable: Object.values(
                                          post.connections,
                                        ).find((conn) => conn.error)?.error
                                          ?.retryable,
                                        maxAttempts: 5,
                                        cancelled: Object.values(
                                          post.connections,
                                        ).find((conn) => conn.error)?.error
                                          ?.cancelled,
                                      }
                                    : undefined
                                }
                              />
                              {/* Date display - hide when showing per-connection breakdown */}
                              {!hasMultipleTimes && !hasMixedStates ? (
                                <div
                                  className={`flex items-center ${dateColorClass}`}
                                >
                                  {dateIcon}
                                  <span>
                                    {multipleTimes.length > 1 ? (
                                      <>
                                        {dateLabel}{" "}
                                        {formatDate(dateValue).split(",")[0]},{" "}
                                        {multipleTimes
                                          .map((t) => formatTimeOnly(t))
                                          .join(", ")}
                                      </>
                                    ) : (
                                      <>
                                        {dateLabel} {formatDate(dateValue)}
                                      </>
                                    )}
                                  </span>
                                </div>
                              ) : null}
                            </div>
                            {analyticsUiEnabled && (
                              <PostMetricsSummary
                                records={analyticsByPostId[post.id]}
                              />
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1 transition-opacity">
                          {isEditable && (
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditAction(post.id);
                              }}
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-gray-600 hover:bg-gray-50"
                              title="Edit Post"
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                          )}

                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDuplicate(post.id);
                            }}
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-50"
                            title="Duplicate Post"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>

                          {getDeleteDialog(
                            post,
                            hasPublished,
                            <Button
                              onClick={(e) => e.stopPropagation()}
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 text-red-600 hover:bg-red-50"
                              title="Delete Post"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>,
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {hasMore && posts.length > 0 && (
            <div className="mt-6 text-center">
              <Button
                onClick={handleLoadMore}
                disabled={fillingPage}
                variant="outline"
              >
                {fillingPage ? "Loading..." : "Load More Posts"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedPostForViewer && (
        <PostViewer
          isOpen={isViewerOpen}
          onClose={() => {
            setIsViewerOpen(false);
            setSelectedPostForViewer(null);
          }}
          expandedPost={selectedPostForViewer}
          connectionsById={connectionsById}
          initialIndex={0}
          mediaUrls={mediaUrls}
        />
      )}
    </div>
  );
}
